/**
 * Correcting one field of one row.
 *
 * The correction is append-only: the extracted value is never overwritten, both stay
 * queryable, and the pair becomes a labelled example. The server validates and
 * normalizes — a money field will not accept a blank, a date must parse — so a
 * reviewer's typo cannot become a "corrected" number that is worse than the original.
 * The rejection reason comes back as text and is shown as-is.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { post } from "../lib/api";
import type { Applied } from "../lib/types";
import { Button } from "./ui";

export function CorrectField({
  rowId,
  field,
  current,
  onDone,
}: {
  rowId: string;
  field: string;
  current: string;
  onDone?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(current);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<Applied | null>(null);
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: () => post<Applied>(`/v1/rows/${rowId}/corrections`, { field, new_value: value }),
    onSuccess: (result) => {
      setApplied(result);
      setError(null);
      setOpen(false);
      // A correction resolves the flag that named the field, so both views are stale.
      void queryClient.invalidateQueries({ queryKey: ["rows"] });
      void queryClient.invalidateQueries({ queryKey: ["exceptions"] });
      void queryClient.invalidateQueries({ queryKey: ["review"] });
      onDone?.();
    },
    onError: (cause) => setError(cause instanceof Error ? cause.message : String(cause)),
  });

  if (!open) {
    return (
      <span className="flex items-center gap-2">
        <button
          onClick={() => {
            setValue(current);
            setError(null);
            setOpen(true);
          }}
          className="font-mono text-[10px] tracking-[0.14em] text-faint uppercase transition-colors hover:text-gold"
        >
          correct
        </button>
        {applied && (
          <span className="font-mono text-[10px] tracking-[0.1em] text-ok uppercase">
            ✓ stored as {applied.normalized}
          </span>
        )}
      </span>
    );
  }

  return (
    <span className="flex flex-wrap items-center gap-2">
      <input
        value={value}
        autoFocus
        spellCheck={false}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") save.mutate();
          if (event.key === "Escape") setOpen(false);
        }}
        className="min-w-0 flex-1 border border-gold bg-ground px-2 py-1 font-mono text-[12px] text-text outline-none"
      />
      <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
        {save.isPending ? "saving…" : "save"}
      </Button>
      <Button variant="ghost" onClick={() => setOpen(false)}>
        cancel
      </Button>
      {error && <span className="w-full font-mono text-[11px] text-crit">&gt; {error}</span>}
    </span>
  );
}
